import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertService, AlertType } from '../../shared/services/alert.service';
import { BookPrintService } from '../../shared/services/apis/platform/book-print.service';
import { BookPrintStruct, BookPrintResponse } from '../../shared/services/responses/platform/book-print.response';
import { CommonModule } from '@angular/common';
import { MenuComponent } from '../../shared/components/menu/menu.component';
import { FooterComponent } from '../../shared/components/footer/footer.component';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { BookVersionResponse, BookVersionStruct } from '../../shared/services/responses/platform/book-version.response';
import { BookVersionService } from '../../shared/services/apis/platform/book-version.service';
import { LoginValidationComponent } from '../login-validation.component/login-validation.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { StatusVersionEnum } from '../../shared/services/enums/status-version.enum';
import { AgreementModalComponent } from '../agreement-modal/agreement-modal.component';
import { AgreementStruct } from '../../shared/services/requests/platform/upload-livro.request';
import { MatDialog } from '@angular/material/dialog';
import { RedirectService } from '../../redirect-service';
import { MatIconModule } from '@angular/material/icon';

export interface PrintGroup {
  size: string;
  variations: BookPrintStruct[];
}

@Component({
  selector: 'app-book-print',
  templateUrl: './book-print.component.html',
  styleUrls: ['./book-print.component.css'],
  standalone: true,
  imports: [CommonModule, MenuComponent, FooterComponent, ReactiveFormsModule, FormsModule, LoginValidationComponent, MatProgressSpinnerModule, MatIconModule],
})
export class BookPrintComponent implements OnInit {
  idBookVersion!: number;
  listBookPrint: BookPrintStruct[] = [];
  groupedPrints: PrintGroup[] = [];
  selectedSizeGroup: PrintGroup | null = null;
  selectedPrint: BookPrintStruct | null = null;
  bookVersion: BookVersionStruct | null = null;
  public isLoading: boolean = false;
  public model: FormGroup;

  constructor(
    private formBuilder: FormBuilder,
    private bookPrintService: BookPrintService,
    private bookVersionService: BookVersionService,
    private route: ActivatedRoute,
    private router: Router,
    private alertService: AlertService,
    private cdr: ChangeDetectorRef,
    private redirectService: RedirectService,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.model = this.formBuilder.group({
      idBookPrint: [, [Validators.required]]
    });

    this.idBookVersion = Number(this.route.snapshot.paramMap.get('idBookVersion'));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  onLoginValidated(isLogged: boolean) {
    if (!isLogged) {
      return;
    }
    this.getBookVersion();
  }

  loadBookPrints(bookVersion: BookVersionStruct): void {
    this.isLoading = true;
    this.bookPrintService.getActive(bookVersion.idBookVersion).subscribe({
      next: (res: BookPrintResponse) => {
        if (res.isError) {
          this.alertService.show('Erro', res.errorDescription, AlertType.error);
          return;
        }

        this.listBookPrint = res.listBookPrintStruct;
        
        // Agrupar por bookPrintSize
        const map = new Map<string, BookPrintStruct[]>();
        for(const print of this.listBookPrint) {
            if(!map.has(print.bookPrintSize)) {
                map.set(print.bookPrintSize, []);
            }
            map.get(print.bookPrintSize)!.push(print);
        }
        this.groupedPrints = Array.from(map.entries()).map(([size, variations]) => ({ size, variations }));

        // Restaurar seleção
        const currentId = bookVersion.idBookPrint;
        if (this.groupedPrints.length > 0) {
          let preSelectGroup = this.groupedPrints[0];
          let preSelectPrint = this.groupedPrints[0].variations[0];
          
          if (currentId) {
            for (const group of this.groupedPrints) {
              const foundPrint = group.variations.find(p => p.idBookPrint === currentId);
              if (foundPrint) {
                preSelectGroup = group;
                preSelectPrint = foundPrint;
                break;
              }
            }
          }
          
          this.selectSizeGroup(preSelectGroup);
        }

        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: err => {
        this.alertService.show('Erro inesperado', err, AlertType.error);
        this.isLoading = false;
      }
    });
  }

  selectSizeGroup(group: PrintGroup): void {
    this.selectedSizeGroup = group;
    if (group.variations.length > 0) {
      this.selectPrint(group.variations[0]);
    } else {
      this.selectedPrint = null;
    }
  }

  selectPrint(print: BookPrintStruct): void {
    this.selectedPrint = print;
    this.model.get('idBookPrint')?.setValue(print.idBookPrint);
  }

  getBookVersion(): void {
    this.isLoading = true;
    this.bookVersionService.GetBookVersion(this.idBookVersion, StatusVersionEnum.Material).subscribe({
      next: (res: BookVersionResponse) => {
        if (res.isError) {
          this.alertService.show('Erro', res.errorDescription, AlertType.error);
          return;
        }

        this.redirectService.redirectByStatus(res.bookVersionStruct.idStatusVersion, this.idBookVersion, res.bookVersionStruct.isCreditReviewing);

        this.bookVersion = res.bookVersionStruct;
        this.loadBookPrints(this.bookVersion);
        this.isLoading = false;
      },
      error: err => {
        this.alertService.show('Erro inesperado', err, AlertType.error);
        this.isLoading = false;
      }
    });
  }

  onRowClick(idBookPrint: number): void {
    this.model.get('idBookPrint')?.setValue(idBookPrint);
  }

  voltar(): void {
    this.router.navigate(['/livro-capa', this.idBookVersion]);
  }

  async publicarAgora(): Promise<void> {
    if (this.model.invalid) {
      this.alertService.show('Atenção', 'Selecione uma opção de impressão.', AlertType.warning);
      return;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // 1. Abrir o modal de acordo antes de prosseguir
    const dialogRef = this.dialog.open(AgreementModalComponent, {
      width: '90%',
      maxHeight: '90vh',
      data: StatusVersionEnum.Material
    });

    const acceptedAgreements: AgreementStruct[] | null = await dialogRef.afterClosed().toPromise();
    // 2. Cancelado ou não aceitou
    if (!acceptedAgreements) {
      this.alertService.show('Erro', 'Você precisa aceitar os termos obrigatórios para continuar.', AlertType.error);
      return;
    }

    this.isLoading = true;

    const request = {
      idBookVersion: this.idBookVersion,
      idBookPrint: this.model.get('idBookPrint')?.value,
      listAgreementStruct: acceptedAgreements
    };

    this.bookPrintService.selectBookPrint(request).subscribe({
      next: (res) => {
        if (res.isError) {
          this.alertService.show('Erro', res.errorDescription, AlertType.error);
          return;
        }

        this.alertService.show('Sucesso', 'Tipo de impressão salvo com sucesso.', AlertType.success);
        this.isLoading = false;
        this.router.navigate(['/livro-pagamento', res.idBookVersion]);
      },
      error: err => {
        this.alertService.show('Erro inesperado', err, AlertType.error);
        this.isLoading = false;
      }
    });
  }
}